import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageRibbon } from '../../components';
import { apiService } from '../../services';
import { useAuth } from '../../context';
import type { AdminUserRow } from '../../types';
import { formatRoleLabel } from '../../utils/classificationLabels';
import type { UserRole } from '../../types/auth';
import '../Dashboard/Dashboard.css';
import './UserRoles.css';

const RolesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const ROLE_OPTIONS: UserRole[] = ['engineer', 'internal_monitor', 'leadership'];

function displayName(row: AdminUserRow): string {
  const name = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
  return name || row.email;
}

function initials(row: AdminUserRow): string {
  const name = displayName(row);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export const UserRoles: React.FC = () => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [pending, setPending] = useState<Record<number, UserRole | ''>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async (q: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.listAdminUsers(q.trim() || undefined);
      if (response.success) setRows(response.data.users);
      else setError(response.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => { load(query); }, 200);
    return () => window.clearTimeout(handle);
  }, [query, load]);

  const summaries = useMemo(() => ([
    { title: 'Engineer', body: 'My Prompts, My Patterns, Leaderboard. Every login has these.' },
    { title: 'Internal monitor', body: 'Lands on Pipeline. Also Hub sync, Last fetch and scoring runs.' },
    { title: 'Leadership', body: 'Lands on Org analytics. Every screen: Score mix, Low-score list, User roles, Pipeline, Hub sync, Last fetch, plus the Engineer screens.' },
  ]), []);

  const save = async (row: AdminUserRow) => {
    const next = pending[row.id];
    if (!next || next === row.role) return;
    setSavingId(row.id);
    setError(null);
    try {
      const response = await apiService.patchAdminUserRole(row.id, next);
      if (!response.success) {
        setError(response.message);
        return;
      }
      setRows((current) => current.map((item) => (item.id === row.id ? response.data : item)));
      setPending((current) => ({ ...current, [row.id]: '' }));
      setToast(`Saved. ${displayName(row)}'s role is now ${formatRoleLabel(next)}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save role');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="dashboard">
      <main className="dashboard__content">
        <PageRibbon
          title="User roles"
          icon={<RolesIcon />}
          subtitle="Choose which role each person has. A change takes effect the next time they open the app."
          actions={
            <input
              className="user-roles__search"
              type="search"
              placeholder="Search by name or email"
              aria-label="Search users"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          }
        />
        <div className="user-roles__cards">
          {summaries.map((item) => (
            <div className="user-roles__role" key={item.title}>
              <b>{item.title}</b>
              <span>{item.body}</span>
            </div>
          ))}
        </div>
        {toast && (
          <div className="user-roles__toast" role="status">{toast}</div>
        )}
        {error && <p className="dashboard__error">{error}</p>}
        <div className="pa-card">
          {loading ? (
            <p className="dashboard__muted" style={{ padding: 16 }}>Loading users…</p>
          ) : (
            <div className="pa-table-wrap">
              <table className="pa-table" style={{ minWidth: 860 }}>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Current role</th>
                    <th>Change to</th>
                    <th className="pa-table__num" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const locked = user?.id === row.id;
                    const chosen = pending[row.id] ?? '';
                    const saveOff = locked || !chosen || chosen === row.role || savingId === row.id;
                    return (
                      <tr key={row.id}>
                        <td>
                          <div className="user-roles__who">
                            <span className="user-roles__av">{initials(row)}</span>
                            <div>
                              <div className="user-roles__name">{displayName(row)}</div>
                              <div className="dashboard__muted">{row.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`pill${row.role === 'leadership' ? '' : ' user-roles__pill-plain'}`}>
                            {formatRoleLabel(row.role)}
                          </span>
                        </td>
                        <td>
                          <select
                            className="user-roles__sel"
                            value={chosen}
                            disabled={locked}
                            aria-label={`New role for ${displayName(row)}`}
                            onChange={(e) => setPending((current) => ({
                              ...current,
                              [row.id]: e.target.value as UserRole | '',
                            }))}
                          >
                            <option value="">Choose a role</option>
                            {ROLE_OPTIONS.map((role) => (
                              <option key={role} value={role} disabled={role === row.role}>
                                {formatRoleLabel(role)}{role === row.role ? ' (current)' : ''}
                              </option>
                            ))}
                          </select>
                          {locked && (
                            <div className="dashboard__muted" style={{ marginTop: 4 }}>
                              You can&apos;t change your own role.
                            </div>
                          )}
                        </td>
                        <td className="pa-table__num">
                          <button
                            type="button"
                            className="user-roles__save"
                            disabled={saveOff}
                            onClick={() => save(row)}
                          >
                            Save
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <p className="dashboard__footnote">
          Each person has one role. The current role is greyed out in the list, and Save stays off until a different role is chosen. Leadership can use this page; nobody can change their own role.
        </p>
      </main>
    </div>
  );
};

export default UserRoles;
